// Mes signalements (D-042) : liste avec le statut vu par l'usager, et fiche de suivi (étapes datées,
// orientation ou motif, photos jointes).
import { STATUTS_SIGNALEMENT_USAGER, type StatutSignalementUsager } from '@cc/contrats';
import { EtatVide, LienBouton } from '@cc/ui';
import { Megaphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date, dateHeure } from '../../../commun/format';
import { EnTeteCitoyen, ExigeConnexion, Ligne, Liste, Section, TexteStatut } from '../commun';
import { chargerPhotoUsager, useMesSignalements, useSignalementUsager, type PhotoSignalement } from './api';
import styles from '../citoyen.module.css';

const TONS: Record<StatutSignalementUsager, 'info' | 'ok' | 'neutre' | 'attention'> = {
  recu: 'info',
  pris_en_compte: 'info',
  planifie: 'info',
  en_cours: 'info',
  termine: 'ok',
  oriente: 'neutre',
  rejete: 'attention',
  doublon: 'neutre',
};

export function StatutSignalement({ code }: { code: StatutSignalementUsager }) {
  return <TexteStatut libelle={STATUTS_SIGNALEMENT_USAGER[code] ?? code} ton={TONS[code] ?? 'neutre'} />;
}

export function PageSignalements() {
  const liste = useMesSignalements();
  return (
    <>
      <EnTeteCitoyen
        titre="Signalements"
        sousTitre="Signalez un lampadaire en panne, un dépôt sauvage ou un problème de voirie, et suivez l'intervention."
        actions={
          <LienBouton href="/citoyen/signalements/nouveau" variante="principal">
            Signaler un problème
          </LienBouton>
        }
      />
      <ExigeConnexion titre="Connectez-vous pour suivre vos signalements">
        <EtatRequete chargement={liste.isPending} erreur={liste.error} onReessayer={() => void liste.refetch()} fonction="Vos signalements" lignes={4}>
          {(liste.data ?? []).length === 0 ? (
            <EtatVide titre="Aucun signalement" icone={Megaphone}>
              <p>Vos signalements et leur suivi apparaîtront ici.</p>
            </EtatVide>
          ) : (
            <Liste libelle="Mes signalements">
              {(liste.data ?? []).map((s) => (
                <Ligne
                  key={s.id}
                  href={`/citoyen/signalements/${s.id}`}
                  titre={
                    <>
                      <span className={styles.mono}>{s.numero}</span> · {s.categorie_libelle}
                    </>
                  }
                  sousTitre={`${s.collectivite.nom} · déposé le ${date(s.cree_le)}${s.adresse ? ` · ${s.adresse}` : ''}`}
                  droite={<StatutSignalement code={s.statut_usager.code} />}
                />
              ))}
            </Liste>
          )}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}

function PhotoUsager({ signalementId, photo }: { signalementId: string; photo: PhotoSignalement }) {
  const [url, setUrl] = useState<string | null>(null);
  const [erreur, setErreur] = useState(false);
  useEffect(() => {
    const c = new AbortController();
    let adresse: string | null = null;
    chargerPhotoUsager(signalementId, photo.id, c.signal)
      .then((u) => {
        adresse = u;
        setUrl(u);
      })
      // Une requête abandonnée (démontage, double montage en développement) n'est pas une erreur.
      .catch(() => {
        if (!c.signal.aborted) setErreur(true);
      });
    return () => {
      c.abort();
      if (adresse) URL.revokeObjectURL(adresse);
    };
  }, [signalementId, photo.id]);
  if (erreur) return <p className={styles.aide}>Photo indisponible.</p>;
  if (!url) return <p className={styles.aide}>Chargement de la photo…</p>;
  return <img src={url} alt={photo.titre} width={160} height={120} style={{ objectFit: 'cover', borderRadius: 4 }} />;
}

export function PageSignalementUsager() {
  const { id = '' } = useParams();
  const fiche = useSignalementUsager(id);
  const s = fiche.data;
  return (
    <>
      <EnTeteCitoyen titre={s ? `Signalement ${s.numero}` : 'Signalement'} surTitre={s?.categorie_libelle} retour="/citoyen/signalements" libelleRetour="Signalements" />
      <ExigeConnexion titre="Connectez-vous pour suivre ce signalement">
        <EtatRequete chargement={fiche.isPending} erreur={fiche.error} onReessayer={() => void fiche.refetch()} fonction="Le suivi du signalement" lignes={5}>
          {s ? (
            <div className={styles.pile}>
              <StatutSignalement code={s.statut_usager.code} />
              <p>{s.description}</p>
              <p className={styles.aide}>
                {s.collectivite.nom}
                {s.adresse ? ` · ${s.adresse}` : ''}
                {s.position ? ` · ${s.position.latitude.toFixed(5)}, ${s.position.longitude.toFixed(5)}` : ''}
              </p>
              {s.organisme_oriente ? <p>Votre signalement a été transmis à : {s.organisme_oriente}.</p> : null}
              {s.motif ? <p>Motif : {s.motif}</p> : null}
              {s.principal ? <p>Ce problème avait déjà été signalé ({s.principal.numero ?? 'signalement principal'}) : vous êtes informé de chaque étape de son traitement.</p> : null}
              <Section titre="Suivi" id="suivi-signalement">
                <ol className={styles.pile} aria-label="Étapes du signalement">
                  {s.suivi.map((e) => (
                    <li key={`${e.code}-${e.le ?? ''}`}>
                      <strong>{e.libelle}</strong>
                      {e.le ? <span className={styles.aide}> · {dateHeure(e.le)}</span> : null}
                    </li>
                  ))}
                </ol>
              </Section>
              <Section titre={`Photos (${s.photos.length})`} id="photos-signalement">
                {s.photos.length === 0 ? <p className={styles.aide}>Aucune photo jointe.</p> : (
                  <ul className={styles.ligneBoutons} aria-label="Photos du signalement">
                    {s.photos.map((p) => (
                      <li key={p.id}>
                        <PhotoUsager signalementId={s.id} photo={p} />
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </div>
          ) : null}
        </EtatRequete>
      </ExigeConnexion>
    </>
  );
}
